import {
  Box,
  ColumnLayout,
  Header,
  Link,
  SpaceBetween,
} from '@cloudscape-design/components';

import type { VoiceSalesCall } from '../../common/api/voiceSales';
import { formatDateString } from '../../common/formatting/dateFormat';
import type { PlaceReportSmall } from '../../common/types';
import { PhoneNumber } from '../../components/PhoneNumber';
import { PlacePeriodList } from '../../components/PlacePeriodList';
import { StarsIcon } from '../../components/StarsIcon';

type VoiceSalesContext = NonNullable<VoiceSalesCall['context']>;
type PhoneLineTypeInfo = VoiceSalesCall['phone_line_type_info'];
type RichContextSection = 'contact' | 'business' | 'scorecard';

export type RichContextProps = {
  context: VoiceSalesContext | null | undefined;
  phoneNumber?: string | null;
  lineTypeInfo?: PhoneLineTypeInfo;
  sections?: RichContextSection[];
  layout?: 'stacked' | 'columns';
};

function contactRole(context: VoiceSalesContext): string | null {
  const values = [context.contact?.title, context.contact?.department].filter(
    (value): value is string => Boolean(value),
  );
  return values.length ? values.join(' · ') : null;
}

function ContactContext({
  context,
  phoneNumber,
  lineTypeInfo,
}: Pick<RichContextProps, 'context' | 'phoneNumber' | 'lineTypeInfo'>) {
  const contact = context?.contact;
  return (
    <SpaceBetween direction="vertical" size="xxxs">
      {contact ? (
        <Header variant="h3">
          {contact.uuid ? (
            <Link href={`/dashboard/contacts/${contact.uuid}`} variant="secondary">
              {contact.name ?? 'Contact name unavailable'}
            </Link>
          ) : (
            contact.name ?? 'Contact name unavailable'
          )}
        </Header>
      ) : (
        <Box color="text-body-secondary">Contact details unavailable</Box>
      )}
      {contact && contactRole(context as VoiceSalesContext) ? (
        <Box variant="span">{contactRole(context as VoiceSalesContext)}</Box>
      ) : null}
      <PhoneNumber
        phoneNumber={phoneNumber}
        lineTypeInfo={lineTypeInfo}
        showLineTypeInfo
      />
    </SpaceBetween>
  );
}

function BusinessContext({ context }: Pick<RichContextProps, 'context'>) {
  const business = context?.business;
  if (!business) {
    return <Box color="text-body-secondary">Business details unavailable</Box>;
  }

  const rating = business.rating;
  const reviewCount = business.total_review_count;
  return (
    <SpaceBetween direction="vertical" size="xxxs">
      <Header variant="h3">
        {business.uuid ? (
          <Link href={`/dashboard/places/${business.uuid}`} variant="secondary">
            {business.name}
          </Link>
        ) : (
          business.name
        )}
      </Header>
      <Box variant="span">{business.address}</Box>
      {rating !== null && rating !== undefined ? (
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          <Box variant="span">{rating.toFixed(1)}</Box>
          <StarsIcon stars={rating} />
          {reviewCount !== null && reviewCount !== undefined ? (
            business.cid_url ? (
              <Link href={business.cid_url} target="_blank" variant="secondary">
                {reviewCount.toLocaleString()} Google reviews
              </Link>
            ) : (
              <Box variant="span" color="text-body-secondary">
                {reviewCount.toLocaleString()} Google reviews
              </Box>
            )
          ) : null}
        </SpaceBetween>
      ) : (
        <Box color="text-body-secondary">Overall rating unavailable</Box>
      )}
    </SpaceBetween>
  );
}

function scorecardReport(context: VoiceSalesContext): PlaceReportSmall | null {
  const scorecard = context.scorecard;
  const reportKey = scorecard?.slug ?? scorecard?.uuid;
  if (!scorecard || !reportKey) return null;

  return {
    report_at: scorecard.report_at,
    hidden: null,
    report_type: null,
    business_type: scorecard.business_type,
    slug: scorecard.slug,
    uuid: scorecard.uuid ?? reportKey,
    created_at: scorecard.report_at,
    updated_at: scorecard.report_at,
    periods: (scorecard.periods ?? []).map((period) => ({
      ...period,
      uuid: period.uuid ?? `${period.range}-${period.start_date}`,
      created_at: period.start_date,
      updated_at: period.end_date,
    })),
  };
}

function ScorecardContext({ context }: Pick<RichContextProps, 'context'>) {
  const scorecard = context?.scorecard;
  if (!scorecard) {
    return <Box color="text-body-secondary">Scorecard details unavailable</Box>;
  }

  const scorecardHref = scorecard.slug
    ? `/${scorecard.slug}`
    : scorecard.uuid
      ? `/${scorecard.uuid}`
      : null;
  const report = context ? scorecardReport(context) : null;
  return (
    <SpaceBetween direction="vertical" size="xxxs">
      <Header variant="h3">
        {scorecardHref ? (
          <Link href={scorecardHref} target="_blank" variant="secondary">
            Scorecard · {formatDateString(scorecard.report_at)}
          </Link>
        ) : (
          <>Scorecard · {formatDateString(scorecard.report_at)}</>
        )}
      </Header>
      <Box variant="span">{scorecard.business_type ?? 'Business type unavailable'}</Box>
      {report ? (
        <PlacePeriodList report={report} name={context?.business?.name ?? 'Scorecard'} hideDownload />
      ) : scorecard.periods?.length ? (
        <Box color="text-body-secondary">Scorecard period links unavailable</Box>
      ) : (
        <Box color="text-body-secondary">No score periods available</Box>
      )}
    </SpaceBetween>
  );
}

export function RichContext({
  context,
  phoneNumber,
  lineTypeInfo,
  sections = ['contact', 'business', 'scorecard'],
  layout = 'stacked',
}: RichContextProps) {
  const content = sections.map((section) => {
    if (section === 'contact') {
      return (
        <ContactContext
          context={context}
          key={section}
          phoneNumber={phoneNumber}
          lineTypeInfo={lineTypeInfo}
        />
      );
    }
    if (section === 'business') return <BusinessContext context={context} key={section} />;
    return <ScorecardContext context={context} key={section} />;
  });

  return layout === 'columns' ? (
    <ColumnLayout columns={sections.length} variant="text-grid">
      {content}
    </ColumnLayout>
  ) : (
    <SpaceBetween direction="vertical" size="s">
      {content}
    </SpaceBetween>
  );
}
